import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface ComboBoxOption<T = any> {
  value: string;
  label: string;
  data?: T; // Additional data for custom rendering
}

interface ComboBoxProps<T = any> {
  options: ComboBoxOption<T>[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  id?: string;
  tabIndex?: number;
  disabled?: boolean;
  height?: string;
  padding?: string;
  fontSize?: string;
  loading?: boolean;
  loadingText?: string;
  renderOption?: (option: ComboBoxOption<T>, isSelected: boolean) => React.ReactNode;
  customFilter?: (option: ComboBoxOption<T>, query: string) => boolean;
}

export const ComboBox: React.FC<ComboBoxProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  className = '',
  searchable = false,
  clearable = false,
  id,
  tabIndex,
  disabled = false,
  height = 'h-auto',
  padding = 'px-4 py-3',
  fontSize = 'text-[17px]',
  loading = false,
  loadingText = 'Loading...',
  renderOption,
  customFilter
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (isOpen && selectedOptionRef.current && dropdownListRef.current) {
      const selectedElement = selectedOptionRef.current;
      const dropdownList = dropdownListRef.current;

      // Calculate the position to scroll to (center the selected item)
      const selectedTop = selectedElement.offsetTop;
      const selectedHeight = selectedElement.offsetHeight;
      const dropdownHeight = dropdownList.clientHeight;

      const scrollTo = selectedTop - (dropdownHeight / 2) + (selectedHeight / 2);

      dropdownList.scrollTop = scrollTo;
    }
  }, [isOpen]);

  const filteredOptions = searchable
    ? options.filter(option =>
        customFilter
          ? customFilter(option, searchQuery)
          : option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const selectedOption = options.find(option => option.value === value);
  const displayValue = searchable && isOpen ? searchQuery : (selectedOption?.label || '');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (searchable && selectedOption) {
      setSearchQuery('');
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      if (isOpen) {
        setIsOpen(false);
        setSearchQuery('');
      } else {
        setIsOpen(true);
        setSearchQuery('');
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
    // Keep dropdown open for seamless reselection
  };

  const showClearButton = clearable && value && !disabled;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`w-full ${padding} ${showClearButton ? 'pr-20' : 'pr-12'} border border-[var(--color-border)] rounded-lg ${fontSize} bg-white focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary)] focus-within:ring-opacity-20 transition-all duration-200 ${height} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} flex items-center`}
        style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
        onClick={() => disabled ? undefined : (searchable ? undefined : setIsOpen(!isOpen))}
        tabIndex={searchable ? undefined : (disabled ? -1 : tabIndex)}
        id={searchable ? undefined : id}
      >
        {searchable ? (
          <input
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className="w-full bg-transparent outline-none text-[var(--color-text-primary)] leading-none"
            style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
            tabIndex={tabIndex}
            id={id}
            disabled={disabled}
          />
        ) : (
          <span className={`leading-none ${value ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}`}>
            {displayValue || placeholder}
          </span>
        )}
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-[var(--color-primary-light)] rounded transition-colors pointer-events-auto"
              title="Clear selection"
            >
              <X className="w-4 h-4 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" />
            </button>
          )}
          <button
            type="button"
            onClick={handleChevronClick}
            className={`p-0.5 ${searchable ? 'pointer-events-auto' : 'pointer-events-none'}`}
          >
            <ChevronDown
              className={`w-5 h-5 text-[var(--color-primary)] transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          ref={dropdownListRef}
          className="absolute z-50 w-full mt-1 bg-white border border-[var(--color-border)] rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="px-4 py-3 text-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--color-primary)] mx-auto mb-2"></div>
              <span className="text-[15px] text-[var(--color-text-muted)]" style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}>
                {loadingText}
              </span>
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <div
                  key={option.value}
                  ref={isSelected ? selectedOptionRef : null}
                  className={`px-4 py-3 cursor-pointer transition-colors ${fontSize} ${
                    isSelected
                      ? 'bg-[var(--color-primary)] !text-[var(--color-primary-text)] hover:bg-[var(--color-primary-hover)]'
                      : 'text-[var(--color-text-primary)] hover:bg-[var(--color-primary-light)]'
                  }`}
                  style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}
                  onClick={() => handleOptionSelect(option.value)}
                >
                  {renderOption ? renderOption(option, isSelected) : option.label}
                </div>
              );
            })
          ) : (
            <div className={`px-4 py-3 text-[var(--color-text-muted)] ${fontSize}`} style={{ fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }}>
              No options found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComboBox;